import React, { useState, useEffect, useMemo } from 'react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell } from 'recharts';

import PerformanceCard from '../analytics-dashboard/components/PerformanceCard';
import TimeRangeSelector from '../analytics-dashboard/components/TimeRangeSelector';
import DateRangeFilter from '../analytics-dashboard/components/DateRangeFilter';
import { ChartSkeleton } from '../../components/ui/SkeletonLoader';
import { showToast } from '../../utils/toast';
import { metricsCollector } from '../../utils/queueMetricsCollector';
import { OPERATION_TYPES } from '../../utils/offlineQueueManager';

import Select from '../../components/ui/Select';
import Button from '../../components/ui/Button';

const DeveloperDashboard = () => {
  const [timeRange, setTimeRange] = useState('7days');
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date?.setDate(date?.getDate() - 7);
    return date?.toISOString()?.split('T')?.[0];
  });
  const [endDate, setEndDate] = useState(() => new Date()?.toISOString()?.split('T')?.[0]);
  const [metrics, setMetrics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filterOperationType, setFilterOperationType] = useState('all');
  const [exportFormat, setExportFormat] = useState('json');

  // Load metrics on mount and when date range changes
  useEffect(() => {
    loadMetrics();
    
    // Set up interval for real-time updates
    const interval = setInterval(() => {
      loadMetrics();
    }, 5000); // Update every 5 seconds

    return () => clearInterval(interval);
  }, [startDate, endDate]);

  // Update date range when time range selector changes
  useEffect(() => {
    const now = new Date();
    let start = new Date();

    switch (timeRange) {
      case '24h':
        start?.setHours(start?.getHours() - 24);
        break;
      case '7days':
        start?.setDate(start?.getDate() - 7);
        break;
      case '30days':
        start?.setDate(start?.getDate() - 30);
        break;
      case 'all':
        start = new Date('2020-01-01');
        break;
      default:
        start?.setDate(start?.getDate() - 7);
    }

    setStartDate(start?.toISOString()?.split('T')?.[0]);
    setEndDate(now?.toISOString()?.split('T')?.[0]);
  }, [timeRange]);

  const loadMetrics = () => {
    setIsLoading(true);
    try {
      let data = metricsCollector?.getMetricsInRange(startDate, endDate);
      setMetrics(data);
    } catch (error) {
      console.error('Failed to load metrics:', error);
      showToast('Failed to load metrics', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    if (!metrics) return null;

    const totalReplays = metrics?.replayDurations?.length || 0;
    const avgReplayTime = totalReplays > 0
      ? (metrics?.replayDurations?.reduce((sum, r) => sum + r?.duration, 0) / totalReplays)?.toFixed(0)
      : 0;

    const totalRetries = metrics?.retryAttempts?.length || 0;
    const successfulRetries = metrics?.retryAttempts?.filter(r => r?.success)?.length || 0;
    const successRate = totalRetries > 0 ? ((successfulRetries / totalRetries) * 100)?.toFixed(1) : 0;

    const totalConflicts = metrics?.conflicts?.length || 0;

    const totalCompression = metrics?.compressionMetrics?.length || 0;
    const totalStorageSaved = totalCompression > 0
      ? metrics?.compressionMetrics?.reduce((sum, c) => sum + c?.storageSaved, 0)
      : 0;

    return {
      totalReplays,
      avgReplayTime,
      successRate,
      totalConflicts,
      totalStorageSaved: (totalStorageSaved / 1024)?.toFixed(2), // Convert to KB
    };
  }, [metrics]);

  // Prepare chart data
  const replayDurationChartData = useMemo(() => {
    if (!metrics?.replayDurations) return [];

    const filtered = filterOperationType === 'all'
      ? metrics?.replayDurations
      : metrics?.replayDurations?.filter(r => r?.operationType === filterOperationType);

    // Group by date
    const grouped = filtered?.reduce((acc, replay) => {
      const date = new Date(replay.timestamp)?.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' });
      if (!acc?.[date]) {
        acc[date] = { date, totalDuration: 0, count: 0 };
      }
      acc[date].totalDuration += replay?.duration;
      acc[date].count++;
      return acc;
    }, {});

    return Object.values(grouped)?.map(g => ({
      date: g?.date,
      avgDuration: g?.count > 0 ? Math.round(g?.totalDuration / g?.count) : 0,
    }));
  }, [metrics, filterOperationType]);

  const retrySuccessChartData = useMemo(() => {
    if (!metrics?.retryAttempts) return [];

    const filtered = filterOperationType === 'all'
      ? metrics?.retryAttempts
      : metrics?.retryAttempts?.filter(r => r?.operationType === filterOperationType);

    const successful = filtered?.filter(r => r?.success)?.length;
    const failed = filtered?.filter(r => !r?.success)?.length;

    return [
      { name: 'Successful', value: successful, color: 'var(--color-success)' },
      { name: 'Failed', value: failed, color: 'var(--color-error)' },
    ];
  }, [metrics, filterOperationType]);

  const conflictFrequencyChartData = useMemo(() => {
    if (!metrics?.conflicts) return [];

    const filtered = filterOperationType === 'all'
      ? metrics?.conflicts
      : metrics?.conflicts?.filter(c => c?.operationType === filterOperationType);

    // Group by date
    const grouped = filtered?.reduce((acc, conflict) => {
      const date = new Date(conflict.timestamp)?.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' });
      if (!acc?.[date]) {
        acc[date] = { date, count: 0 };
      }
      acc[date].count++;
      return acc;
    }, {});

    return Object.values(grouped);
  }, [metrics, filterOperationType]);

  const compressionRatioChartData = useMemo(() => {
    if (!metrics?.compressionMetrics) return [];

    const filtered = filterOperationType === 'all'
      ? metrics?.compressionMetrics
      : metrics?.compressionMetrics?.filter(c => c?.operationType === filterOperationType);

    // Group by date
    const grouped = filtered?.reduce((acc, compression) => {
      const date = new Date(compression.timestamp)?.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' });
      if (!acc?.[date]) {
        acc[date] = { date, totalRatio: 0, count: 0, totalSaved: 0 };
      }
      acc[date].totalRatio += compression?.compressionRatio;
      acc[date].totalSaved += compression?.storageSaved;
      acc[date].count++;
      return acc;
    }, {});

    return Object.values(grouped)?.map(g => ({
      date: g?.date,
      avgRatio: g?.count > 0 ? (g?.totalRatio / g?.count)?.toFixed(1) : 0,
      storageSaved: (g?.totalSaved / 1024)?.toFixed(2), // KB
    }));
  }, [metrics, filterOperationType]);

  const retryAttemptsDistribution = useMemo(() => {
    if (!metrics?.retryAttempts) return [];

    const filtered = filterOperationType === 'all'
      ? metrics?.retryAttempts
      : metrics?.retryAttempts?.filter(r => r?.operationType === filterOperationType);

    const distribution = filtered?.reduce((acc, retry) => {
      const attempt = `Attempt ${retry?.attemptNumber}`;
      if (!acc?.[attempt]) {
        acc[attempt] = { name: attempt, successful: 0, failed: 0 };
      }
      if (retry?.success) {
        acc[attempt].successful++;
      } else {
        acc[attempt].failed++;
      }
      return acc;
    }, {});

    return Object.values(distribution);
  }, [metrics, filterOperationType]);

  const conflictTypeBreakdown = useMemo(() => {
    if (!metrics?.conflicts) return [];

    const filtered = filterOperationType === 'all'
      ? metrics?.conflicts
      : metrics?.conflicts?.filter(c => c?.operationType === filterOperationType);

    const breakdown = filtered?.reduce((acc, conflict) => {
      const type = conflict?.conflictType || 'Unknown';
      if (!acc?.[type]) {
        acc[type] = { name: type, value: 0 };
      }
      acc[type].value++;
      return acc;
    }, {});

    return Object.values(breakdown);
  }, [metrics, filterOperationType]);

  const handleExport = (category = 'all') => {
    try {
      let data;
      let filename;

      if (category === 'all') {
        data = metrics;
        filename = `queue-metrics-all-${startDate}-to-${endDate}`;
      } else if (category === 'replayDurations') {
        data = metrics?.replayDurations;
        filename = `replay-durations-${startDate}-to-${endDate}`;
      } else if (category === 'retryAttempts') {
        data = metrics?.retryAttempts;
        filename = `retry-attempts-${startDate}-to-${endDate}`;
      } else if (category === 'conflicts') {
        data = metrics?.conflicts;
        filename = `conflicts-${startDate}-to-${endDate}`;
      } else if (category === 'compressionMetrics') {
        data = metrics?.compressionMetrics;
        filename = `compression-metrics-${startDate}-to-${endDate}`;
      }

      if (exportFormat === 'json') {
        const jsonStr = JSON.stringify(data, null, 2);
        const blob = new Blob([jsonStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${filename}.json`;
        a?.click();
        URL.revokeObjectURL(url);
      } else if (exportFormat === 'csv') {
        const csv = metricsCollector?.exportAsCSV(category === 'all' ? 'replayDurations' : category);
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${filename}.csv`;
        a?.click();
        URL.revokeObjectURL(url);
      }

      showToast(`Exported ${category} metrics as ${exportFormat?.toUpperCase()}`, 'success');
    } catch (error) {
      console.error('Export failed:', error);
      showToast('Failed to export metrics', 'error');
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm font-interface font-semibold text-foreground mb-1">{label}</p>
          {payload?.map((entry, index) => (
            <p key={index} className="text-sm font-interface text-muted-foreground">
              {entry?.name}: <span className="font-semibold" style={{ color: entry?.color }}>{entry?.value}</span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const operationTypeOptions = [
    { label: 'All Operations', value: 'all' },
    { label: 'Bookmarks', value: OPERATION_TYPES?.BOOKMARK },
    { label: 'Claps', value: OPERATION_TYPES?.CLAP },
    { label: 'Comments', value: OPERATION_TYPES?.COMMENT },
    { label: 'Story Updates', value: OPERATION_TYPES?.STORY_UPDATE },
  ];

  const exportFormatOptions = [
    { label: 'JSON', value: 'json' },
    { label: 'CSV', value: 'csv' },
  ];

  if (isLoading && !metrics) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <div className="h-8 bg-muted rounded w-64 mb-2" />
            <div className="h-4 bg-muted rounded w-96" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
            {[1, 2, 3, 4]?.map(i => (
              <div key={i} className="card-elevated">
                <div className="h-4 bg-muted rounded w-24 mb-2" />
                <div className="h-8 bg-muted rounded w-32" />
              </div>
            ))}
          </div>
          <ChartSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-2">
            Developer Dashboard
          </h1>
          <p className="text-base md:text-lg font-interface text-muted-foreground">
            Monitor offline queue system performance and metrics
          </p>
        </div>

        {/* Time Range Selector */}
        <div className="mb-6">
          <TimeRangeSelector
            activeRange={timeRange}
            onRangeChange={setTimeRange}
          />
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
          <PerformanceCard
            title="Total Replays"
            value={summaryStats?.totalReplays || 0}
            icon="RefreshCw"
            iconColor="#9B8DC7"
            change={0}
            changeType="neutral"
          />
          <PerformanceCard
            title="Avg Success Rate"
            value={`${summaryStats?.successRate || 0}%`}
            icon="CheckCircle"
            iconColor="var(--color-success)"
            change={0}
            changeType="positive"
          />
          <PerformanceCard
            title="Total Conflicts"
            value={summaryStats?.totalConflicts || 0}
            icon="AlertTriangle"
            iconColor="var(--color-warning)"
            change={0}
            changeType="neutral"
          />
          <PerformanceCard
            title="Storage Saved"
            value={`${summaryStats?.totalStorageSaved || 0} KB`}
            icon="HardDrive"
            iconColor="var(--color-accent)"
            change={0}
            changeType="neutral"
          />
        </div>

        {/* Filters */}
        <div className="card-elevated mb-6">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="flex-1 max-w-xs">
              <Select
                label="Filter by Operation Type"
                options={operationTypeOptions}
                value={filterOperationType}
                onChange={setFilterOperationType}
              />
            </div>
            <div className="flex gap-3 items-end">
              <div className="w-32">
                <Select
                  label="Export Format"
                  options={exportFormatOptions}
                  value={exportFormat}
                  onChange={setExportFormat}
                />
              </div>
              <Button
                onClick={() => handleExport('all')}
                iconName="Download"
                variant="secondary"
              >
                Export All
              </Button>
            </div>
          </div>
        </div>

        {/* Date Range Filter */}
        <div className="mb-8">
          <DateRangeFilter
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onExport={() => handleExport('all')}
          />
        </div>

        {/* Queue Replay Duration */}
        <div className="card-elevated mb-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h3 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-2">
                Queue Replay Duration
              </h3>
              <p className="text-sm font-interface text-muted-foreground">
                Average replay time over selected period
              </p>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleExport('replayDurations')}
              iconName="Download"
            >
              Export
            </Button>
          </div>
          <div className="w-full h-64 md:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={replayDurationChartData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis
                  dataKey="date"
                  stroke="var(--color-muted-foreground)"
                  style={{ fontSize: '12px', fontFamily: 'Inter, sans-serif' }}
                />
                <YAxis
                  stroke="var(--color-muted-foreground)"
                  style={{ fontSize: '12px', fontFamily: 'Inter, sans-serif' }}
                  label={{ value: 'Duration (ms)', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone"
                  dataKey="avgDuration"
                  name="Avg Duration (ms)"
                  stroke="#9B8DC7"
                  strokeWidth={3}
                  dot={{ fill: '#9B8DC7', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Retry Success Rates & Attempts Distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Retry Success Pie Chart */}
          <div className="card-elevated">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h3 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-2">
                  Retry Success Rates
                </h3>
                <p className="text-sm font-interface text-muted-foreground">
                  Success vs failure distribution
                </p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleExport('retryAttempts')}
                iconName="Download"
              >
                Export
              </Button>
            </div>
            <div className="w-full h-64 md:h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={retrySuccessChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100)?.toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {retrySuccessChartData?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry?.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Retry Attempts Distribution */}
          <div className="card-elevated">
            <div className="mb-6">
              <h3 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-2">
                Retry Attempts Distribution
              </h3>
              <p className="text-sm font-interface text-muted-foreground">
                Success rate by attempt number
              </p>
            </div>
            <div className="w-full h-64 md:h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={retryAttemptsDistribution} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis
                    dataKey="name"
                    stroke="var(--color-muted-foreground)"
                    style={{ fontSize: '12px', fontFamily: 'Inter, sans-serif' }}
                  />
                  <YAxis
                    stroke="var(--color-muted-foreground)"
                    style={{ fontSize: '12px', fontFamily: 'Inter, sans-serif' }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="successful" name="Successful" fill="var(--color-success)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="failed" name="Failed" fill="var(--color-error)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Conflict Frequency & Type Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Conflict Frequency Bar Chart */}
          <div className="card-elevated">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h3 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-2">
                  Conflict Frequency
                </h3>
                <p className="text-sm font-interface text-muted-foreground">
                  Conflicts per day
                </p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleExport('conflicts')}
                iconName="Download"
              >
                Export
              </Button>
            </div>
            <div className="w-full h-64 md:h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={conflictFrequencyChartData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis
                    dataKey="date"
                    stroke="var(--color-muted-foreground)"
                    style={{ fontSize: '12px', fontFamily: 'Inter, sans-serif' }}
                  />
                  <YAxis
                    stroke="var(--color-muted-foreground)"
                    style={{ fontSize: '12px', fontFamily: 'Inter, sans-serif' }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Conflicts" fill="var(--color-warning)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Conflict Type Breakdown */}
          <div className="card-elevated">
            <div className="mb-6">
              <h3 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-2">
                Conflict Type Breakdown
              </h3>
              <p className="text-sm font-interface text-muted-foreground">
                Distribution by conflict type
              </p>
            </div>
            <div className="w-full h-64 md:h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={conflictTypeBreakdown}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100)?.toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {conflictTypeBreakdown?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={`hsl(${index * 45}, 70%, 60%)`} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Batch Compression Metrics */}
        <div className="card-elevated mb-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h3 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-2">
                Batch Compression Metrics
              </h3>
              <p className="text-sm font-interface text-muted-foreground">
                Compression ratio and storage saved over time
              </p>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleExport('compressionMetrics')}
              iconName="Download"
            >
              Export
            </Button>
          </div>
          <div className="w-full h-64 md:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={compressionRatioChartData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis
                  dataKey="date"
                  stroke="var(--color-muted-foreground)"
                  style={{ fontSize: '12px', fontFamily: 'Inter, sans-serif' }}
                />
                <YAxis
                  yAxisId="left"
                  stroke="var(--color-muted-foreground)"
                  style={{ fontSize: '12px', fontFamily: 'Inter, sans-serif' }}
                  label={{ value: 'Ratio (%)', angle: -90, position: 'insideLeft' }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke="var(--color-muted-foreground)"
                  style={{ fontSize: '12px', fontFamily: 'Inter, sans-serif' }}
                  label={{ value: 'Saved (KB)', angle: 90, position: 'insideRight' }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="avgRatio"
                  name="Avg Compression Ratio (%)"
                  stroke="#9B8DC7"
                  fill="rgba(155, 141, 199, 0.3)"
                />
                <Area
                  yAxisId="right"
                  type="monotone"
                  dataKey="storageSaved"
                  name="Storage Saved (KB)"
                  stroke="#D4A574"
                  fill="rgba(212, 165, 116, 0.3)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Operation Stats by Type */}
        <div className="card-elevated">
          <div className="mb-6">
            <h3 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-2">
              Operation Statistics by Type
            </h3>
            <p className="text-sm font-interface text-muted-foreground">
              Success and failure counts for each operation type
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 font-interface font-semibold text-foreground">Operation Type</th>
                  <th className="text-right py-3 px-4 font-interface font-semibold text-foreground">Total</th>
                  <th className="text-right py-3 px-4 font-interface font-semibold text-foreground">Success</th>
                  <th className="text-right py-3 px-4 font-interface font-semibold text-foreground">Failed</th>
                  <th className="text-right py-3 px-4 font-interface font-semibold text-foreground">Success Rate</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(metrics?.operationStats || {})?.map(([type, stats]) => {
                  const successRate = stats?.total > 0 ? ((stats?.success / stats?.total) * 100)?.toFixed(1) : 0;
                  return (
                    <tr key={type} className="border-b border-border hover:bg-muted/50 transition-colors">
                      <td className="py-3 px-4 font-interface text-foreground capitalize">{type?.replace('_', ' ')}</td>
                      <td className="text-right py-3 px-4 font-interface text-foreground">{stats?.total}</td>
                      <td className="text-right py-3 px-4 font-interface text-success">{stats?.success}</td>
                      <td className="text-right py-3 px-4 font-interface text-error">{stats?.failed}</td>
                      <td className="text-right py-3 px-4 font-interface font-semibold text-foreground">{successRate}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeveloperDashboard;
